import { Notyf } from 'notyf';

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('.contracts-list-page');
    if (!page) return;

    initSelection();
    initSortHeaders();
    initSearch();
    initExportLink();
    initLazyStats(page);
});

// ── Selection ─────────────────────────────────────────────────────────────

let selectedIds = new Set<string>();

function initSelection() {
    const checkAll = document.getElementById('clp-check-all');
    const tbody    = document.getElementById('clp-tbody');
    const bulkBar  = document.getElementById('clp-bulk-bar');
    const bulkCount = document.getElementById('clp-bulk-count');
    const bulkClear = document.getElementById('clp-bulk-clear');

    if (!checkAll || !tbody || !bulkBar || !bulkCount || !bulkClear) return;

    // Row checkboxes
    tbody.querySelectorAll<HTMLElement>('.clp-row-check').forEach(box => {
        box.addEventListener('click', () => toggleRow(box));
        box.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleRow(box); }
        });
    });

    // Header checkbox
    checkAll.addEventListener('click', () => {
        const allBoxes = Array.from(tbody.querySelectorAll<HTMLElement>('.clp-row-check'));
        const allChecked = allBoxes.every(b => b.classList.contains('checked'));
        if (allChecked) {
            allBoxes.forEach(b => { b.classList.remove('checked'); deselectRow(b); });
        } else {
            allBoxes.forEach(b => { b.classList.add('checked'); selectRow(b); });
        }
        updateBulkBar();
        checkAll.classList.toggle('checked', !allChecked);
    });
    checkAll.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); checkAll.click(); }
    });

    // Clear selection
    bulkClear.addEventListener('click', () => {
        clearSelection();
    });

    // Esc key clears selection
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && selectedIds.size > 0) clearSelection();
    });

    function toggleRow(box: HTMLElement) {
        const tr = box.closest('tr');
        if (!tr) return;
        const id = (tr as HTMLElement).dataset.id ?? '';
        if (selectedIds.has(id)) {
            selectedIds.delete(id);
            box.classList.remove('checked');
            tr.classList.remove('clp-tr--selected');
        } else {
            selectedIds.add(id);
            box.classList.add('checked');
            tr.classList.add('clp-tr--selected');
        }
        updateBulkBar();
    }

    function selectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        const id = tr.dataset.id ?? '';
        selectedIds.add(id);
        tr.classList.add('clp-tr--selected');
    }

    function deselectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        const id = tr.dataset.id ?? '';
        selectedIds.delete(id);
        tr.classList.remove('clp-tr--selected');
    }

    function updateBulkBar() {
        const n = selectedIds.size;
        bulkBar!.style.display = n > 0 ? 'flex' : 'none';
        if (bulkCount) bulkCount.textContent = `${n} ${bulkCount.textContent?.replace(/^\d+\s/, '') ?? 'selected'}`;

        // sync header check
        const allBoxes = Array.from(tbody!.querySelectorAll<HTMLElement>('.clp-row-check'));
        const allChecked = allBoxes.length > 0 && allBoxes.every(b => b.classList.contains('checked'));
        checkAll!.classList.toggle('checked', allChecked);
    }

    function clearSelection() {
        selectedIds.clear();
        tbody!.querySelectorAll<HTMLElement>('.clp-row-check').forEach(b => {
            b.classList.remove('checked');
            b.closest('tr')?.classList.remove('clp-tr--selected');
        });
        checkAll!.classList.remove('checked');
        bulkBar!.style.display = 'none';
    }

    // Bulk action buttons
    bulkBar.querySelectorAll<HTMLElement>('[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
            const action = btn.dataset.action;
            if (action === 'export') {
                exportSelected();
            } else {
                const notyf = new Notyf({ duration: 3000 });
                notyf.open({ type: 'warning', message: 'Coming soon' });
            }
        });
    });
}

// ── Export ────────────────────────────────────────────────────────────────

function initExportLink() {
    // The page-head Export link already works as a GET — no JS needed.
    // The bulk bar Export button calls exportSelected() below.
}

function exportSelected() {
    const exportUrl: string = (window as any).__clpExportUrl ?? '/Contract/ExportCsv';
    const filters: Record<string, string> = (window as any).__clpCurrentFilters ?? {};

    const params = new URLSearchParams(filters);
    if (selectedIds.size > 0) {
        params.set('ids', Array.from(selectedIds).join(','));
    }

    window.location.href = `${exportUrl}?${params.toString()}`;
}

// ── Sort headers ──────────────────────────────────────────────────────────

function initSortHeaders() {
    const form      = document.getElementById('clp-filter-form-main') as HTMLFormElement | null;
    const sortInput = document.getElementById('clp-sort-order-main') as HTMLInputElement | null;
    const pageInput = document.getElementById('clp-page-input') as HTMLInputElement | null;
    if (!form || !sortInput) return;

    document.querySelectorAll<HTMLElement>('.clp-th--sortable').forEach(th => {
        th.addEventListener('click', () => {
            const key     = th.dataset.sortKey ?? '';
            const current = sortInput.value;
            const next    = current === key ? `${key}_desc` : key;
            sortInput.value = next;
            if (pageInput) pageInput.value = '0';
            form.submit();
        });
    });
}

// ── Search debounce ───────────────────────────────────────────────────────

function initSearch() {
    const form  = document.getElementById('clp-filter-form-main') as HTMLFormElement | null;
    const input = document.getElementById('clp-search-input') as HTMLInputElement | null;
    const pageInput = document.getElementById('clp-page-input') as HTMLInputElement | null;
    if (!form || !input) return;

    let timer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (pageInput) pageInput.value = '0';
            form.submit();
        }, 280);
    });
}

// ── Lazy stats & status counts ────────────────────────────────────────────

interface ContractListStatsDTO {
    revenueThisRange: number;
    revenuePreviousRange: number;
    travellersBooked: number;
    travellersContracts: number;
    awaitingPaymentAmount: number;
    awaitingPaymentContracts: number;
    overdueContracts: number;
    upcomingDepartures: number;
    currency: string;
}

async function fetchJSON<T>(url: string): Promise<T | null> {
    try {
        const r = await fetch(url, { headers: { Accept: 'application/json' } });
        const j = await r.json();
        return j.success ? (j.data as T) : null;
    } catch { return null; }
}

function fmt(n: number): string {
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function initLazyStats(page: HTMLElement) {
    const urlStats        = page.dataset.urlStats ?? '';
    const urlStatusCounts = page.dataset.urlStatusCounts ?? '';
    const s = (window as any).__clpStrings ?? {};

    loadStats(urlStats, s);
    loadStatusCounts(urlStatusCounts);
}

async function loadStats(url: string, s: Record<string, string>) {
    const grid = document.getElementById('clp-stats-grid');
    if (!grid || !url) return;

    const data = await fetchJSON<ContractListStatsDTO>(url);
    if (!data) return;

    const revDelta = data.revenuePreviousRange > 0
        ? Math.round((data.revenueThisRange - data.revenuePreviousRange) / data.revenuePreviousRange * 1000) / 10
        : 0;
    const revUp = revDelta >= 0;

    grid.innerHTML = `
        <div class="clp-stat-card">
            <div class="clp-stat-label">
                <div class="clp-stat-icon" style="background:var(--clp-accent-light);color:var(--clp-accent)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18.5 4.5C17 3.5 15 3 13 3 8 3 4 7 4 12s4 9 9 9c2 0 4-.5 5.5-1.5"/><path d="M3 10h12M3 14h10"/></svg>
                </div>
                ${s.statsRevenue ?? ''}
            </div>
            <div class="clp-stat-value">${fmt(data.revenueThisRange)} ${data.currency}</div>
            <div class="clp-stat-foot">
                <span class="clp-stat-delta ${revUp ? 'up' : 'down'}">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style="${revUp ? '' : 'transform:rotate(180deg)'}"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>
                    ${Math.abs(revDelta)}%
                </span>
                <span>${(s.statsRevenueVs ?? '').replace('{0}', fmt(data.revenuePreviousRange) + ' ' + data.currency)}</span>
            </div>
            <svg class="clp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10 L120 42 L0 42 Z" fill="#F5A623" opacity="0.10"/>
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10" stroke="#F5A623" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="clp-stat-card">
            <div class="clp-stat-label">
                <div class="clp-stat-icon" style="background:var(--clp-info-light);color:var(--clp-info)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                ${s.statsTravellers ?? ''}
            </div>
            <div class="clp-stat-value">${data.travellersBooked}</div>
            <div class="clp-stat-foot">
                <span>${(s.statsTravellersAcross ?? '').replace('{0}', String(data.travellersContracts))}</span>
            </div>
            <svg class="clp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12 L120 42 L0 42 Z" fill="#1A56DB" opacity="0.10"/>
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12" stroke="#1A56DB" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="clp-stat-card">
            <div class="clp-stat-label">
                <div class="clp-stat-icon" style="background:var(--clp-danger-light);color:var(--clp-danger)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                ${s.statsAwaiting ?? ''}
            </div>
            <div class="clp-stat-value">${fmt(data.awaitingPaymentAmount)} ${data.currency}</div>
            <div class="clp-stat-foot">
                <span>${(s.statsAwaitingFoot ?? '').replace('{0}', String(data.awaitingPaymentContracts)).replace('{1}', String(data.overdueContracts))}</span>
            </div>
            <svg class="clp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8 L120 42 L0 42 Z" fill="#FA5252" opacity="0.10"/>
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8" stroke="#FA5252" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="clp-stat-card">
            <div class="clp-stat-label">
                <div class="clp-stat-icon" style="background:var(--clp-success-light);color:var(--clp-success)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>
                </div>
                ${s.statsUpcoming ?? ''}
            </div>
            <div class="clp-stat-value">${data.upcomingDepartures}</div>
            <div class="clp-stat-foot">
                <span>${s.statsUpcomingFoot ?? ''}</span>
            </div>
            <svg class="clp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 28 L15 22 L30 26 L60 18 L80 10 L100 14 L120 8 L120 42 L0 42 Z" fill="#05A677" opacity="0.10"/>
                <path d="M0 28 L15 22 L30 26 L60 18 L80 10 L100 14 L120 8" stroke="#05A677" stroke-width="1.6" fill="none"/>
            </svg>
        </div>`;
}

async function loadStatusCounts(url: string) {
    if (!url) return;

    const data = await fetchJSON<Record<string, number>>(url);
    if (!data) return;

    let total = 0;
    for (const count of Object.values(data)) total += count;

    document.querySelectorAll<HTMLElement>('.clp-seg__count[data-status-key]').forEach(el => {
        const key = el.dataset.statusKey ?? '';
        if (key === '') {
            el.textContent = String(total);
        } else {
            const n = data[key] ?? 0;
            el.textContent = n > 0 ? String(n) : '';
        }
    });
}
